import { api } from '@/api';
import { createEntityAttachmentApi } from '@/api/common';
import type {
  CreateRepairRequest,
  RepairHistoryEntry,
  UpdateRepairRequest,
} from '../types';

// repairAttachments exposes the shared entity-attachment endpoints for one repair entry.
// Reused by the upload hook (create + enable). Opening a document uses the dedicated
// fetchRepairDocumentUrl endpoint below instead.
export function repairAttachments(repairId: string) {
  return createEntityAttachmentApi(`/v1/repair-history/${repairId}`);
}

// fetchDeviceRepairs returns every repair entry of a device, newest handling left to the UI.
export async function fetchDeviceRepairs(deviceId: string): Promise<RepairHistoryEntry[]> {
  return api.get<RepairHistoryEntry[]>(`/v1/devices/${deviceId}/repair-history`);
}

// createRepair adds a new repair entry and returns it (its id is needed to upload the document).
export async function createRepair(request: CreateRepairRequest): Promise<RepairHistoryEntry> {
  return api.post<RepairHistoryEntry>('/v1/repair-history', request);
}

// updateRepair patches the entry fields; only the changed subset should be sent.
export async function updateRepair(
  repairId: string,
  request: UpdateRepairRequest
): Promise<RepairHistoryEntry> {
  return api.patch<RepairHistoryEntry>(`/v1/repair-history/${repairId}`, request);
}

// deleteRepair removes a repair entry and its document.
export async function deleteRepair(repairId: string): Promise<void> {
  return api.delete<void>(`/v1/repair-history/${repairId}`);
}

// fetchRepairDocumentUrl resolves the signed download URL for a repair's single document.
export async function fetchRepairDocumentUrl(repairId: string): Promise<string> {
  const response = await api.get<{ url: string }>(
    `/v1/repair-history/${repairId}/document-url`
  );
  return response.url;
}
